// Public league sign-up (mini-reg). No account needed.
//
// Same shape as the day-pass quick checkout: the player enters their details,
// picks a team or registers as a free agent, and goes straight to Stripe. The
// server creates or reuses a `mini_` user keyed on their email, so a returning
// player is the same person rather than a second registration.
//
// Nothing here decides money or a place in the league. The price shown is read
// from the league for display only; createLeagueMiniRegistration re-reads the
// league and settles the charge, the capacity and the wait-list itself.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, MenuItem,
  Checkbox, FormControlLabel, Link,
} from '@mui/material';
import { usePageMeta } from '../utils/pageMeta';
import { describeLeagueFormat } from '../utils/leagueFormat';
import { loadRequiredWaiver, waiverHref } from '../utils/waiver';
import { isJuniorLeague, missingJuniorFields, ageOn } from '../utils/juniors';
import {
  FREE_AGENT, NEW_TEAM, JOIN_TEAM, teamChoiceFields,
} from '../utils/leagueTeamChoice';
import { GENDERS, impliedGender, genderLabel } from '../utils/leagueGender';
import { localToday } from '../utils/dates';

const gold = '#C9A84C';
const inputSx = {
  '& .MuiInputBase-input': { color: 'white' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(201,168,76,0.35)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gold },
  '& .MuiSvgIcon-root': { color: 'rgba(201,168,76,0.7)' },
};

const fmtDate = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US',
    { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function LeagueSignup() {
  usePageMeta('League Sign-up | {club}',
    'Register for a league in under a minute — no account required.');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const leagueId = params.get('league') || '';

  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', notes: '',
    guardianName: '', birthdate: '',      // junior leagues only
    // The public form is how a junior the club has never met arrives, so it
    // always asks: an athlete record is created for them, and a coach on the
    // sand needs somebody to call and to know what the child reacts to.
    ecName: '', ecPhone: '', allergies: '',
  });
  const [teamChoice, setTeamChoice] = useState(FREE_AGENT);
  const [newTeamName, setNewTeamName] = useState('');
  const [joinTeamId, setJoinTeamId] = useState('');
  const [gender, setGender] = useState('');
  const [waiver, setWaiver] = useState(null);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  // League sign-up collects a phone and never asked what may be sent to it.
  // Unticked is silence, not a refusal — the server only ever writes a yes.
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!leagueId) { setLoadError('No league specified.'); return; }
        const l = await getDoc(doc(db, 'leagues', leagueId));
        if (!l.exists()) { setLoadError('League not found.'); return; }
        const data = { id: l.id, ...l.data() };
        if (data.status !== 'registration') {
          setLoadError('Registration is not open for this league.');
          return;
        }
        if (cancelled) return;
        setLeague(data);
        // Teams are public to read; the dropdown is only a convenience and the
        // server re-checks the team belongs to this league and has room.
        const w = await loadRequiredWaiver(data);
        if (!cancelled) setWaiver(w);
        const t = await getDocs(query(collection(db, 'leagueTeams'),
          where('leagueId', '==', leagueId)));
        if (!cancelled) {
          setTeams(t.docs.map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''),
              undefined, { numeric: true })));
        }
      } catch (e) {
        if (!cancelled) setLoadError('Could not load this league. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  const juniors = isJuniorLeague(league);
  const juniorGaps = missingJuniorFields(league, form);
  const playerAge = ageOn(form.birthdate);
  // Preview of the server's own fallback, so a blank name box is not a mystery.
  // Junior pairs are named by first name, so that is what the hint promises.
  const fallbackFirstName = String(form.firstName || '').trim() || 'the first name above';

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'createLeagueMiniRegistration');
      const { data } = await fn({
        leagueId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        notes: form.notes.trim(),
        gender,
        ...teamChoiceFields(teamChoice, newTeamName, joinTeamId),
        waiverAccepted,
        smsConsent,
        // Junior leagues only. The server ignores them for an adult league, so
        // no registration carries a guardian nobody was asked for.
        ...(juniors ? {
          guardianName: form.guardianName.trim(),
          birthdate: form.birthdate,
          emergencyContact: { name: form.ecName.trim(), phone: form.ecPhone.trim() },
          allergies: form.allergies.trim(),
        } : {}),
      });
      if (data.url) { window.location.href = data.url; return; }
      setDone(data);
    } catch (e) {
      setError(e.message || 'Registration failed. Please try again.');
    }
    setSubmitting(false);
  };

  const priceCents = Number(league?.price) || 0;
  // An adult team must be named before it can be created. Juniors may leave it
  // blank — the pair takes its players' first names.
  const teamNameMissing = teamChoice === NEW_TEAM && !juniors && !newTeamName.trim();
  // Only asked on leagues where it is a real question, so only required there.
  const genderMissing = !impliedGender(league?.leagueType) && !gender;
  const complete = form.firstName.trim() && form.lastName.trim()
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) && form.phone.trim()
    && juniorGaps.length === 0
    // The club is meeting this player for the first time through this form, so
    // these are asked here rather than chased later.
    && (!juniors || (form.ecName.trim() && form.ecPhone.trim() && form.allergies.trim()))
    && !teamNameMissing
    && !genderMissing
    && (!waiver || (waiverAccepted && !waiver.unreadable));

  const shell = (children) => (
    <Box sx={{ minHeight: '100vh', background: '#0D1E35', py: 6, px: 2 }}>
      <Box sx={{ maxWidth: 480, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img src="/horzlogo.png" alt="Torch Beach Volleyball"
            style={{ maxWidth: 240, width: '60vw', objectFit: 'contain' }} />
        </Box>
        {children}
      </Box>
    </Box>
  );

  if (loading) return shell(
    <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: gold }} /></Box>
  );

  if (loadError) return shell(
    <>
      <Alert severity="info" sx={{ mb: 2 }}>{loadError}</Alert>
      <Button fullWidth onClick={() => navigate('/tournaments')} sx={{ color: gold }}>
        See what else is on
      </Button>
    </>
  );

  if (done) return shell(
    <Alert severity={done.status === 'waitlisted' ? 'warning' : 'success'}>
      {done.alreadyRegistered
        ? 'You are already registered for this league.'
        : done.status === 'waitlisted'
          ? `This league is full — you're on the wait-list at position ${done.waitlistPosition}. `
            + 'We’ll be in touch if a spot opens, and you have not been charged.'
          : done.createdTeam
            ? `You're registered and ${done.teamName} is your team — you're the captain. `
              + 'Check your email for the confirmation.'
            : `You're registered${done.teamName ? ` with ${done.teamName}` : ' as a free agent'}. `
              + 'Check your email for the confirmation.'}
    </Alert>
  );

  return shell(
    <>
      <Typography sx={{ color: 'white', fontSize: '1.6rem', fontWeight: 700, mb: 0.5 }}>
        {league.name}
      </Typography>
      {league.dayTimeDisplay && (
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', mb: 2 }}>{league.dayTimeDisplay}</Typography>
      )}
      <Box sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', mb: 3 }}>
        <div>{league.format}s · {league.numWeeks} week{Number(league.numWeeks) === 1 ? '' : 's'} from {fmtDate(league.startDate)}</div>
        <div>{describeLeagueFormat(league)}</div>
        {priceCents > 0 && (
          <div style={{ color: gold, fontWeight: 600, marginTop: 8 }}>
            ${(priceCents / 100).toFixed(2)} per player
          </div>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* On a junior league the player and the person filling this in are two
          different people, so every field says whose it is. */}
      <TextField fullWidth label={juniors ? "Player's first name" : 'First name'}
        value={form.firstName} onChange={set('firstName')} sx={{ ...inputSx, mb: 2 }} />
      <TextField fullWidth label={juniors ? "Player's last name" : 'Last name'}
        value={form.lastName} onChange={set('lastName')} sx={{ ...inputSx, mb: 2 }} />
      {juniors && (
        <>
          <TextField fullWidth label="Parent or guardian" value={form.guardianName}
            onChange={set('guardianName')} sx={{ ...inputSx, mb: 2 }} />
          <TextField fullWidth label="Emergency contact" value={form.ecName}
            onChange={set('ecName')} sx={{ ...inputSx, mb: 2 }}
            helperText="Who we call if we cannot reach the parent"
            FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }} />
          <TextField fullWidth label="Emergency contact phone" value={form.ecPhone}
            onChange={set('ecPhone')} sx={{ ...inputSx, mb: 2 }} />
          <TextField fullWidth label="Allergies" value={form.allergies}
            onChange={set('allergies')} sx={{ ...inputSx, mb: 2 }}
            helperText="Enter None if there are none"
            FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }} />
          <TextField fullWidth type="date" label="Player's date of birth"
            value={form.birthdate} onChange={set('birthdate')}
            InputLabelProps={{ shrink: true, sx: { color: 'rgba(255,255,255,0.5)' } }}
            inputProps={{ max: localToday() }}
            helperText={playerAge != null ? `${playerAge} years old` : ' '}
            FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }}
            sx={{ ...inputSx, mb: 2 }} />
        </>
      )}
      <TextField fullWidth label={juniors ? "Parent's email" : 'Email'} type="email"
        value={form.email} onChange={set('email')} sx={{ ...inputSx, mb: 2 }}
        helperText={juniors ? 'Where the confirmation goes' : ''}
        FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }} />
      <TextField fullWidth label={juniors ? "Parent's phone" : 'Phone'}
        value={form.phone} onChange={set('phone')} sx={{ ...inputSx, mb: 2 }} />

      {!impliedGender(league?.leagueType) && (
        <TextField fullWidth select label={genderLabel(juniors)} value={gender}
          onChange={e => setGender(e.target.value)} sx={{ ...inputSx, mb: 2 }}
          SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          helperText="Used to build balanced teams"
          FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.45)' } }}>
          <MenuItem value="">Select…</MenuItem>
          {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
        </TextField>
      )}

      <TextField fullWidth select label="Team" value={teamChoice}
        onChange={e => setTeamChoice(e.target.value)} sx={{ ...inputSx, mb: 1 }}>
        <MenuItem value={FREE_AGENT}>Free agent — place me on a team</MenuItem>
        <MenuItem value={JOIN_TEAM}>Join an existing team</MenuItem>
        <MenuItem value={NEW_TEAM}>
          {juniors ? 'Start a new team — the player is captain' : "Start a new team — I'm the captain"}
        </MenuItem>
      </TextField>
      {teamChoice === JOIN_TEAM && (
        <>
          <TextField fullWidth select label="Which team? (optional)" value={joinTeamId}
            onChange={e => setJoinTeamId(e.target.value)}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, mt: 1.5, mb: 1 }}>
            <MenuItem value="">Not sure yet — put me on the available list</MenuItem>
            {teams.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </TextField>
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', mb: 3 }}>
            {joinTeamId
              ? 'You will be added to that team when you register.'
              : teams.length
                ? "Leave this blank and your name goes on the league's available players list, where any captain can pick you up."
                : "No teams have been started yet. Leave this blank and your name goes on the available players list for the first captain who needs a player."}
          </Typography>
        </>
      )}
      {teamChoice === NEW_TEAM ? (
        <>
          <TextField fullWidth required={!juniors}
            label={juniors ? 'Team name (optional)' : 'Team name'} value={newTeamName}
            onChange={e => setNewTeamName(e.target.value)} sx={{ ...inputSx, mt: 1.5, mb: 1 }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', mb: 3 }}>
            {juniors
              ? (newTeamName.trim()
                  ? 'You can pick a different name now.'
                  : `Leave blank and the team is named after its players — ${fallbackFirstName} until a partner joins, then both first names.`)
              : 'Every 4s team needs a name. You can change it later.'}
            {' '}Registering this way has no login, so the club changes the name for you
            later — sign in before registering if you'd rather manage the team yourself.
          </Typography>
        </>
      ) : teamChoice === JOIN_TEAM ? null : (
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', mb: 3 }}>
          Register as a free agent and the club will place you — a captain can also
          pick you off the available players list.
        </Typography>
      )}

      <TextField fullWidth label="Anything we should know? (optional)" multiline minRows={2}
        value={form.notes} onChange={set('notes')} sx={{ ...inputSx, mb: 3 }} />

      {waiver?.unreadable && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This league's waiver can't be shown here. Please contact the club to register.
        </Alert>
      )}
      {waiver && !waiver.unreadable && (
        <FormControlLabel
          sx={{ alignItems: 'flex-start', mb: 2,
            '.MuiFormControlLabel-label': { fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)' } }}
          control={<Checkbox checked={waiverAccepted}
            onChange={e => setWaiverAccepted(e.target.checked)}
            sx={{ color: gold, '&.Mui-checked': { color: gold }, pt: 0 }} />}
          label={<span>
            I have read and accept the{' '}
            <Link href={waiverHref(waiver)} target="_blank" rel="noopener" sx={{ color: gold }}>
              {waiver.name}
            </Link>.
          </span>} />
      )}

      <FormControlLabel
        sx={{ alignItems: 'flex-start', mb: 2,
          '.MuiFormControlLabel-label': { fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)' } }}
        control={<Checkbox checked={smsConsent}
          onChange={e => setSmsConsent(e.target.checked)}
          sx={{ color: gold, '&.Mui-checked': { color: gold }, pt: 0 }} />}
        label={juniors
          ? 'Text this number about schedule changes, rain calls and results. Optional — message and data rates may apply.'
          : 'Text me about schedule changes, rain calls and results. Optional — message and data rates may apply.'} />

      <Button fullWidth variant="contained" disabled={!complete || submitting} onClick={submit}
        sx={{ background: gold, color: '#0D1E35', fontWeight: 700, py: 1.3,
          '&:hover': { background: '#d8b95c' } }}>
        {submitting ? 'Working…'
          : priceCents > 0 ? `Register — $${(priceCents / 100).toFixed(2)}` : 'Register'}
      </Button>
      <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', mt: 2, textAlign: 'center' }}>
        Already have an account? Members may pay a lower rate — sign in first and register from the league page.
      </Typography>
    </>
  );
}
